import { ProductFormData } from '@/@types/Products';
import { useAuth } from '@/auth/Auth';
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect';
import { Input } from '@/components/shared/Input/Input';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { normalizeUrlWhenPaste } from '@/utils/normalize';
import { Loader2 } from 'lucide-react';

import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Step5Props {
  watch: UseFormWatch<ProductFormData>;
  register: UseFormRegister<ProductFormData>;
  errors: FieldErrors<ProductFormData>;
  setValue: UseFormSetValue<ProductFormData>;
  clearErrors: UseFormClearErrors<ProductFormData>;
  defaultValues: ProductFormData;
  handleBack: () => void;
  handleNext: () => void;
  isSubmitting?: boolean;
}

export default function Step5({
  watch,
  errors,
  setValue,
  defaultValues,
  handleBack,
  handleNext,
  isSubmitting = false,
  register,
}: Step5Props) {
  const { t } = useTranslation(['common', 'onboarding', 'affiliates']);
  const { authData } = useAuth();

  const deliveryOptions = [
    {
      value: 'no_delivery',
      label: t('common:delivery.external'),
      isDisabled: false,
    },
    {
      value: 'attach_files',
      label: t('common:delivery.files'),
      isDisabled: watch('product_type') !== 'digital',
    },
    {
      value: 'external_member_area',
      label: t('common:delivery.member_area'),
      isDisabled:
        !authData?.member_area_enabled || watch('product_type') !== 'digital',
    },
  ];

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleNext();
      }}
      className='w-full max-w-lg'
    >
      <h1 className='text-2xl font-bold text-center mb-6'>
        {t('step5Title')}
        <p className='text-interactive'>
          {t('delivered')} <strong className='font-normal text-white'>?</strong>{' '}
        </p>
      </h1>
      <CustomSelect
        placeholder={t('deliveryMethodPlaceholder')}
        options={deliveryOptions?.filter((option) => !option.isDisabled)}
        label={t('deliveryMethodLabel')}
        onValueChange={(value) => setValue('product_delivery', value)}
        defaultValue={defaultValues?.product_delivery}
        value={defaultValues?.product_delivery}
        helperText={errors?.product_delivery?.message}
        id='deliveryMethod'
      />

      {defaultValues?.product_type === 'digital' &&
        watch('product_delivery') === 'no_delivery' && (
          <div className='space-y-4 mt-4'>
            <Alert variant='info'>
              <p className='text-sm'>
                {t('step5AlertMessage', {
                  email: watch('product_email') || '<email>',
                  password: watch('product_password') || '<password>',
                })}
              </p>
            </Alert>
            <Input
              label={t('productAccessLink')}
              {...register('product_url')}
              placeholder={t('productAccessLinkPlaceholder')}
              subLabel={t('productAccessLinkInfo')}
              tooltip={t('productAccessLinkTooltip')}
              helperText={errors?.product_url?.message}
              data-cy='product-url'
              onFocus={(e) => {
                if (!e.target.value) {
                  e.target.value = 'https://';
                  setValue('product_url', 'https://');
                }
              }}
              onPaste={(e) => {
                const finalValue = normalizeUrlWhenPaste(e);
                setValue('product_url', finalValue);
              }}
            />
            <Input
              label={t('productEmail')}
              {...register('product_email')}
              placeholder={t('productEmailPlaceholder')}
              helperText={errors?.product_email?.message}
              data-cy='product-email'
              autoComplete='off'
            />
            <Input
              label={t('productPassword')}
              {...register('product_password')}
              placeholder={t('productPasswordPlaceholder')}
              helperText={errors?.product_password?.message}
              type='password'
              data-cy='product-password'
              autoComplete='new-password'
            />
            <div>
              <Label htmlFor='product_extras' className='mb-2 block'>
                {t('productExtras')}{' '}
                <span className='text-sm text-muted'>
                  ({t('affiliates:optional')})
                </span>
              </Label>
              <Textarea
                {...register('product_extras')}
                placeholder={t('productExtrasPlaceholder')}
                className={`focus:outline-none ring-0 focus-visible:outline-none ${
                  errors?.product_extras ? 'border-error' : 'border-colorBorder'
                }`}
                data-cy='product-extras-textarea'
              />

              {errors?.product_extras?.message && (
                <p className='text-sm text-error'>
                  {errors.product_extras.message}
                </p>
              )}
            </div>
          </div>
        )}

      {watch('product_delivery') === 'external_member_area' && (
        <Alert variant='info' className='mt-4'>
          <p className='text-sm'>{t('common:delivery.member_area_info')}</p>
        </Alert>
      )}

      <div className='flex justify-center gap-6 mt-6'>
        <Button type='button' onClick={handleBack} variant='ghost'>
          {t('back')}
        </Button>

        <Button data-cy='submit-product' type='submit' variant='default'>
          {isSubmitting ? (
            <span className='flex items-center gap-2'>
              <Loader2 className='animate-spin' />
              {t('saving')}
            </span>
          ) : (
            t('finish')
          )}
        </Button>
      </div>
    </form>
  );
}
